import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import { getAziendaUsage } from '../../lib/services/statusService'; // Importuj servis za plan
import DashboardSidebarAdmin from '../../components/navigation/DashboardSidebarAdmin';
import DashboardHeader from '../../components/navigation/DashboardHeader';
import DashboardFooter from '../../components/navigation/DashboardFooter';
import MobileMenuButton from '../../components/navigation/MobileMenuButton';
import WelcomeCard from './components/WelcomeCard';
import StatCard from './components/StatCard';
import QuickActionCard from './components/QuickActionCard';
import RecentActivityCard from './components/RecentActivityCard';
import ListaDipendenti from '../../components/navigation/ListaDipendenti';

const HomeDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  
  const [recentActivities, setRecentActivities] = useState([]);
  const [myDocsCount, setMyDocsCount] = useState('...');
  const [totalEmployees, setTotalEmployees] = useState('...');
  const [unreadNotifications, setUnreadNotifications] = useState('0');
  const [currentPlan, setCurrentPlan] = useState('...'); 

  const [adminData, setAdminData] = useState({
    id_azienda: null,
    nome: '',
    ruolo: ''
  });

  useEffect(() => {
    fetchAdminDataAndStats();
  }, [t]);

  const fetchAdminDataAndStats = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        const { data: profilo, error: profError } = await supabase
          .from('profili')
          .select('nome, cognome, id_azienda, ruolo')
          .eq('id', user.id)
          .single();

        if (profilo && !profError) {
          const fullName = `${profilo.nome} ${profilo.cognome}`;
          setAdminData({
            id_azienda: profilo.id_azienda,
            nome: fullName,
            ruolo: profilo.ruolo
          });

          const resUsage = await getAziendaUsage(profilo.id_azienda);
          if (resUsage.success) {
            setCurrentPlan(resUsage.data.plan || 'Basic');
          }

          if (user.last_sign_in_at) {
            setRecentActivities([
              {
                id: 1,
                type: 'login',
                title: t('home_dipendente.activities.login_title'),
                description: t('home_dipendente.activities.login_desc'),
                timestamp: new Date(user.last_sign_in_at),
                user: { name: fullName, avatar: "" }
              }
            ]);
          }

          const { count: empCount, error: empError } = await supabase
            .from('profili')
            .select('*', { count: 'exact', head: true })
            .eq('id_azienda', profilo.id_azienda);

          if (!empError) setTotalEmployees(empCount.toString());
        }

        // BROJAČ 1: Moji dokumenti (MODIFIKOVANO: samo is_deleted = false)
        const { count: docCount, error: countError } = await supabase
          .from('documenti')
          .select('*', { count: 'exact', head: true })
          .eq('id_utente', user.id)
          .eq('is_deleted', false); // NOVO

        if (!countError) setMyDocsCount(docCount.toString());

        // BROJAČ 2: Nepročitane notifikacije (MODIFIKOVANO: samo is_deleted = false)
        const { count: unreadCount, error: unreadError } = await supabase
          .from('documenti')
          .select('*', { count: 'exact', head: true })
          .eq('id_utente', user.id)
          .eq('procitano', false)
          .eq('is_deleted', false); // NOVO

        if (!unreadError) setUnreadNotifications(unreadCount.toString());
      }
    } catch (err) {
      console.error("Greška:", err.message);
      setMyDocsCount('0');
      setTotalEmployees('0');
      setUnreadNotifications('0');
      setCurrentPlan('N/A');
    }
  };
  
  const statsData = [
    {
      id: 1,
      title: 'home_superadmin.stats.active_sessions', 
      value: myDocsCount,
      icon: 'FileText',
      iconColor: 'var(--color-primary)',
      onClick: () => navigate('/my-documents')
    },
    {
      id: 2,
      title: 'home_superadmin.stats.total_employees', 
      value: totalEmployees,
      change: '+2',
      changeType: 'positive',
      icon: 'Users',
      iconColor: 'var(--color-success)',
      onClick: () => console.log('Employees list')
    },
    {
      id: 3,
      title: 'home_superadmin.stats.pending_notif',
      value: unreadNotifications,
      icon: 'Bell',
      iconColor: 'var(--color-warning)',
      onClick: () => navigate('/my-documents')
    },
    {
      id: 4,
      title: 'home_superadmin.stats.avg_time', 
      value: (
        <div className="flex flex-col items-start gap-2 py-1">
          <span className="text-[0.85em] uppercase tracking-[0.15em] font-black leading-tight text-foreground">
            {currentPlan}
          </span>
          <span className="text-[12px] font-normal text-muted-foreground leading-tight">
            {t('home_superadmin.stats.check_usage')}
          </span>
        </div>
      ),
      icon: 'Activity', 
      iconColor: 'var(--color-accent)',
      onClick: () => navigate('/stats') 
    }
  ];

  const quickActions = [
    {
      id: 1,
      title: t('home_superadmin.actions.profile.title'),
      description: t('home_superadmin.actions.profile.desc'),
      icon: 'User',
      iconColor: 'var(--color-primary)',
      onClick: () => navigate('/mio-profilo')
    },
    {
      id: 2,
      title: t('home_superadmin.actions.settings.title'),
      description: t('home_superadmin.actions.settings.desc'),
      icon: 'Settings',
      iconColor: 'var(--color-secondary)',
      onClick: () => navigate('/settings')
    },
    {
      id: 5,
      title: t('home_superadmin.actions.create_employee.title'),
      description: t('home_superadmin.actions.create_employee.desc'),
      icon: 'UserPlus',
      iconColor: 'var(--color-success)',
      onClick: () => navigate('/crea-dipendente')
    },
{
       id: 7,
      title: t('home_superadmin.actions.holidays_management.title'),
      description: t('home_superadmin.actions.holidays_management.desc'),
      icon: 'CalendarCheck',
      iconColor: '#f59e0b',
      onClick: () => navigate('/admin-holidays')
    },

 {
          id: 6, 
      title: t('home_superadmin.actions.inventory.title'), 
      description: t('home_superadmin.actions.inventory.desc'), 
      icon: 'Database', 
      iconColor: '#1fa65aff', 
      onClick: () => navigate('/home-inventory') 
    },
  

{
      id: 4,
      title: t('home_superadmin.actions.trash.title'),
      description: t('home_superadmin.actions.trash.desc'),
      icon: 'Trash2',
      iconColor: '#ef4444', // Direktna crvena boja da iskače (red-500)
      onClick: () => navigate('/restore-documents')
    }
  ];

  const handleLogout = () => navigate('/landing-authentication');
  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <MobileMenuButton isOpen={isSidebarOpen} onClick={toggleSidebar} />
      <DashboardSidebarAdmin isCollapsed={!isSidebarOpen} onToggle={toggleSidebar} />
      <DashboardHeader onLogout={handleLogout} userName={adminData.nome || 'Admin'} />

      <main className="main-content with-sidebar">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
          <WelcomeCard userName={adminData.nome} />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mt-6 md:mt-8">
            {statsData.map((stat) => (
              <StatCard key={stat.id} {...stat} />
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">
            <div className="lg:col-span-2">
              <ListaDipendenti idAzienda={adminData.id_azienda} />
            </div>

            <div className="space-y-6 md:space-y-8">
              <div>
                <h2 className="text-xl font-black uppercase tracking-tighter mb-4">
                  {t('home_superadmin.sections.quick_actions')}
                </h2>
                <div className="grid grid-cols-1 gap-4">
                  {quickActions.map((action) => (
                    <QuickActionCard key={action.id} {...action} />
                  ))}
                </div>
              </div>
              <RecentActivityCard activities={recentActivities} />
            </div>
          </div>
        </div>
      </main>
      <DashboardFooter />
    </div>
  );
};

export default HomeDashboard;